/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo, FC } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import {
  Camera,
  Tag,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Check,
  FileText,
  FileImage,
  File,
  Download,
  Paperclip,
  X,
  Upload,
  ArrowUpDown,
  ArrowDown,
  ArrowUp,
  Eye,
  Share2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import {
  useHotspotObjects,
  useDeleteNote,
  useDeleteOverlay,
  useDeleteHotspot,
  useCreateNoteAttachment,
  useUpdateHotspot,
  useHotspotOverlayGroups,
} from '../hooks/hooks';
import FABActionDialog from '../fab_menu/FABActionDialog';
import { Hotspot, HotspotObject, Note, PhotoMarker } from '../types/types';
import BackButton from './BackButton';
import {
  getObjectIcon,
  getObjectTypeColor,
  getObjectTypeBackground,
  getObjectTypePrimaryColor,
} from '../utils/hotspotUtils';
import { toast } from '@/shared/hooks/use-toast';
import ImageEditForm from '../fab_menu/forms/ImageEditForm';
import SharePhotoDialog from './SharePhotoDialog';
import { ENABLE_PHOTO_GROUPS } from '../constants/constants';

interface HotspotObjectsDialogProps {
  open: boolean;
  onClose: () => void;
  hotspot: Hotspot | null;
  onObjectUpdated?: () => Promise<void>;
  onObjectDeleted?: (objectId: number, objectType: string) => Promise<void>;
  projectId?: string;
  onCloseDialogs?: () => void;
  markerNumber?: number;
  layoutImageUrl?: string;
  layoutImageDimensions?: { width: number; height: number };
  photoMarkers?: PhotoMarker[];
}

type ViewState = 'list' | 'note' | 'photo' | 'label' | 'attachment';

const HotspotObjectsDialog: FC<HotspotObjectsDialogProps> = ({
  open,
  onClose,
  hotspot,
  onObjectUpdated,
  onObjectDeleted,
  projectId,
  markerNumber,
  layoutImageUrl,
  layoutImageDimensions,
  photoMarkers,
}) => {
  const [showAddObjectDialog, setShowAddObjectDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareObject, setShareObject] = useState<HotspotObject | null>(null);
  const [shareObjects, setShareObjects] = useState<HotspotObject[]>([]);
  const [shareGroupId, setShareGroupId] = useState<number | null>(null);
  const [currentView, setCurrentView] = useState<ViewState>('list');
  const [selectedObject, setSelectedObject] = useState<HotspotObject | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [editingImageData, setEditingImageData] = useState<{
    hotspotId: number;
    overlayId: number;
    title: string;
    imageUrl: string;
  } | null>(null);

  // State for editing hotspot title
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [currentTitle, setCurrentTitle] = useState('');

  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState<FileList | null>(null);
  const attachmentUploadRef = useRef<HTMLDivElement>(null);

  const [viewingAttachment, setViewingAttachment] = useState<{
    url: string;
    filename: string;
    contentType: string;
    noteTitle: string;
  } | null>(null);

  type SortOption =
    | 'title-asc'
    | 'title-desc'
    | 'date-asc'
    | 'date-desc'
    | 'type-asc'
    | 'type-desc';
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  const [visibleTypes, setVisibleTypes] = useState<Set<string>>(
    new Set(['note', 'image', 'label'])
  );

  const toggleTypeVisibility = (type: string) => {
    setVisibleTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const {
    objects: hotspotObjects,
    isLoading: objectsLoading,
    error: objectsError,
    refetch: refetchObjects,
  } = useHotspotObjects(hotspot?.id, projectId);

  const availableTypes = useMemo(() => {
    if (!hotspotObjects) return [];
    const types = [...new Set(hotspotObjects.map((obj) => obj.type))];
    return types.sort();
  }, [hotspotObjects]);

  const deleteNoteMutation = useDeleteNote(projectId);
  const deleteOverlayMutation = useDeleteOverlay();
  const deleteHotspotMutation = useDeleteHotspot();
  const createNoteAttachmentMutation = useCreateNoteAttachment();
  const updateHotspotMutation = useUpdateHotspot(projectId);
  useHotspotOverlayGroups(ENABLE_PHOTO_GROUPS ? hotspot?.id : undefined);

  const getFileIcon = useCallback((filename: string, contentType: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();

    if (
      contentType.startsWith('image/') ||
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')
    ) {
      return <FileImage className="h-4 w-4 text-blue-500" />;
    }

    if (contentType.includes('pdf') || extension === 'pdf') {
      return <FileText className="h-4 w-4 text-red-500" />;
    }

    if (contentType.includes('text') || ['txt', 'md', 'csv'].includes(extension || '')) {
      return <FileText className="h-4 w-4 text-muted-foreground" />;
    }

    if (['doc', 'docx'].includes(extension || '')) {
      return <FileText className="h-4 w-4 text-blue-600" />;
    }

    return <File className="h-4 w-4 text-muted-foreground" />;
  }, []);

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  useEffect(() => {
    if (open) {
      setCurrentView('list');
      setSelectedObject(null);
      setIsEditing(false);
      setIsEditingImage(false);
      setEditingImageData(null);
      setIsEditingTitle(false);
      setEditingTitle('');
      setCurrentTitle(hotspot?.title || '');
      setIsUploadingAttachment(false);
      setSelectedAttachmentFiles(null);
      setViewingAttachment(null);
      setVisibleTypes(new Set(['note', 'image', 'label']));
    }
  }, [open, hotspot?.id]);

  // Only sync currentTitle on initial load, not during optimistic updates
  useEffect(() => {
    if (hotspot && open) {
      setCurrentTitle(hotspot.title || '');
    }
  }, [hotspot?.id, open]); // Only when dialog opens or hotspot changes, not on label changes

  useEffect(() => {
    if (isEditing && selectedObject?.type === 'note') {
      setTimeout(() => {
        const firstInput = document.querySelector('form input[name="title"]') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
          firstInput.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  }, [isEditing, selectedObject]);

  useEffect(() => {
    if (isEditingImage && editingImageData) {
      setTimeout(() => {
        const imageEditInput = document.getElementById('edit-image-title');
        if (imageEditInput) {
          imageEditInput.focus();
          imageEditInput.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  }, [isEditingImage, editingImageData]);

  useEffect(() => {
    if (selectedObject && hotspotObjects) {
      const updatedObject = hotspotObjects.find(
        (obj) => obj.id === selectedObject.id && obj.type === selectedObject.type
      );
      if (updatedObject && JSON.stringify(updatedObject) !== JSON.stringify(selectedObject)) {
        setSelectedObject(updatedObject);
      }
    }
  }, [hotspotObjects, selectedObject]);

  useEffect(() => {
    if (isUploadingAttachment && attachmentUploadRef.current) {
      setTimeout(() => {
        attachmentUploadRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [isUploadingAttachment]);

  const handleAddObject = useCallback(() => {
    setShowAddObjectDialog(true);
  }, []);

  const handleObjectClick = useCallback((object: HotspotObject) => {
    setIsEditing(false);
    setIsEditingImage(false);
    setEditingImageData(null);
    setIsUploadingAttachment(false);
    setSelectedAttachmentFiles(null);

    setSelectedObject(object);
    setCurrentView(object.type as ViewState);
  }, []);

  const handleBackToList = useCallback(() => {
    setIsEditing(false);
    setIsEditingImage(false);
    setEditingImageData(null);
    setIsEditingTitle(false);
    setEditingTitle('');
    setCurrentTitle(hotspot?.title || '');
    setIsUploadingAttachment(false);
    setSelectedAttachmentFiles(null);
    setViewingAttachment(null);

    setCurrentView('list');
    setSelectedObject(null);
  }, [hotspot]);

  const handleViewAttachment = useCallback(
    (
      attachment: { download_url: string; original_filename: string; content_type: string },
      noteTitle: string
    ) => {
      setViewingAttachment({
        url: attachment.download_url,
        filename: attachment.original_filename,
        contentType: attachment.content_type,
        noteTitle: noteTitle,
      });
      setCurrentView('attachment');
    },
    []
  );

  const handleBackToNote = useCallback(() => {
    setViewingAttachment(null);
    setCurrentView('note');
  }, []);

  const isViewableAttachment = useCallback((contentType: string, filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    return (
      contentType.startsWith('image/') ||
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '') ||
      contentType.includes('pdf') ||
      extension === 'pdf'
    );
  }, []);

  const handleEdit = useCallback(
    (object: HotspotObject) => {
      if (object.type === 'note') {
        setIsEditing(true);
      } else if (object.type === 'image' && typeof object.id === 'number') {
        const imageData = (object as HotspotObject & { rawData?: { url?: string } })?.rawData;
        if (imageData && hotspot) {
          setEditingImageData({
            hotspotId: hotspot.id,
            overlayId: object.id,
            title: object.title,
            imageUrl: imageData.url || '',
          });
          setIsEditingImage(true);
        }
      }
    },
    [hotspot]
  );

  const handleUploadAttachments = useCallback(async () => {
    if (!selectedAttachmentFiles || !selectedObject || selectedObject.type !== 'note' || !hotspot)
      return;

    const noteData = selectedObject.rawData as Note;

    try {
      const uploadPromises = Array.from(selectedAttachmentFiles).map((file) =>
        createNoteAttachmentMutation.mutateAsync({
          noteId: noteData.id,
          file,
          hotspotId: hotspot.id,
        })
      );

      await Promise.all(uploadPromises);

      setIsUploadingAttachment(false);
      setSelectedAttachmentFiles(null);

      toast({
        title: 'Success!',
        description: `${selectedAttachmentFiles.length} file(s) uploaded successfully.`,
        variant: 'default',
        className: 'bg-green-500 text-white',
      });

      if (onObjectUpdated) {
        await onObjectUpdated();
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);

      toast({
        title: 'Error',
        description: 'Failed to upload attachments. Please try again.',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
      });
    }
  }, [
    selectedAttachmentFiles,
    selectedObject,
    hotspot,
    createNoteAttachmentMutation,
    onObjectUpdated,
    toast,
  ]);

  const handleEditTitle = useCallback(() => {
    setEditingTitle(currentTitle);
    setIsEditingTitle(true);
  }, [currentTitle]);

  const handleSaveTitle = useCallback(async () => {
    if (!hotspot || !editingTitle.trim()) return;

    const newTitle = editingTitle.trim();

    // Immediately update the UI
    setCurrentTitle(newTitle);
    setIsEditingTitle(false);
    setEditingTitle('');

    try {
      await updateHotspotMutation.mutateAsync({
        hotspotId: hotspot.id,
        data: { title: newTitle },
      });

      toast({
        title: 'Success!',
        description: 'Hotspot title updated successfully.',
        variant: 'default',
        className: 'bg-green-500 text-white',
      });

      if (onObjectUpdated) {
        await onObjectUpdated();
      }
    } catch (error) {
      console.error('Error updating hotspot title:', error);

      // Revert the title on error
      setCurrentTitle(hotspot.title || '');

      toast({
        title: 'Error',
        description: 'Failed to update hotspot title. Please try again.',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
      });
    }
  }, [hotspot, editingTitle, updateHotspotMutation, onObjectUpdated, toast]);

  const handleCancelTitleEdit = useCallback(() => {
    setIsEditingTitle(false);
    setEditingTitle('');
    // No need to revert currentTitle since we haven't changed it yet
  }, []);

  const handleDelete = useCallback(
    async (object: HotspotObject) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete this ${object.type.replace('_', ' ')}?`
      );
      if (!confirmed) return;

      try {
        if (selectedObject?.id === object.id) {
          handleBackToList();
        }

        if (object.type === 'note' && typeof object.id === 'number') {
          await deleteNoteMutation.mutateAsync(object.id);
        } else if (object.type === 'image' && typeof object.id === 'number' && hotspot) {
          await deleteOverlayMutation.mutateAsync({
            hotspotId: hotspot.id,
            overlayId: object.id,
          });
        }

        if (onObjectDeleted && typeof object.id === 'number') {
          await onObjectDeleted(object.id, object.type);
        }
      } catch (error) {
        console.error('Error deleting object:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete object. Please try again.',
          variant: 'destructive',
          className: 'bg-red-500 text-white',
        });
      }
    },
    [selectedObject, deleteNoteMutation, deleteOverlayMutation, hotspot, onObjectDeleted, toast]
  );

  const handleDeleteHotspot = useCallback(async () => {
    if (!hotspot || !projectId) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this hotspot? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      await deleteHotspotMutation.mutateAsync({
        hotspotId: hotspot.id,
        projectId: projectId,
      });

      toast({
        title: 'Success!',
        description: 'Hotspot deleted successfully.',
        variant: 'default',
        className: 'bg-green-500 text-white',
      });

      onClose();
    } catch (error) {
      console.error('Error deleting hotspot:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete hotspot. Please try again.',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
      });
    }
  }, [hotspot, projectId, deleteHotspotMutation, toast, onClose]);

  const handleShare = useCallback((object: HotspotObject, groupId?: number | null) => {
    if (object.type !== 'image') return;
    setShareObject(object);
    setShareObjects([object]);
    setShareGroupId(groupId ?? ((object.rawData as { group_id?: number | null })?.group_id ?? null));
    setShowShareDialog(true);
  }, []);

  const handleShareGroup = useCallback(
    (objects: HotspotObject[], groupId?: number | null) => {
      const firstImageObject = objects.find((object) => object.type === 'image');
      if (!firstImageObject) return;
      setShareObject(firstImageObject);
      setShareObjects(objects.filter((object) => object.type === 'image'));
      setShareGroupId(groupId ?? null);
      setShowShareDialog(true);
    },
    []
  );

  const sortedObjects = useMemo(() => {
    if (!hotspotObjects) return [];

    const filtered = hotspotObjects.filter((obj) => visibleTypes.has(obj.type));
    const sorted = [...filtered];

    switch (sortBy) {
      case 'title-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'title-desc':
        return sorted.sort((a, b) => b.title.localeCompare(a.title));
      case 'date-asc':
        return sorted.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case 'date-desc':
        return sorted.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'type-asc':
        return sorted.sort((a, b) => a.type.localeCompare(b.type));
      case 'type-desc':
        return sorted.sort((a, b) => b.type.localeCompare(a.type));
      default:
        return sorted;
    }
  }, [hotspotObjects, sortBy, visibleTypes]);

  const groupedImageObjects = useMemo(() => {
    const images = sortedObjects.filter((object) => object.type === 'image');
    const grouped = new Map<
      string,
      { key: string; groupId: number | null; groupName: string | null; items: HotspotObject[] }
    >();

    images.forEach((object) => {
      const imageData = object.rawData as { group_id?: number | null; group_name?: string | null };
      const groupId = imageData?.group_id ?? null;
      const key = groupId ? `group-${groupId}` : `single-${object.id}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.items.push(object);
      } else {
        grouped.set(key, {
          key,
          groupId,
          groupName: imageData?.group_name ?? null,
          items: [object],
        });
      }
    });

    return Array.from(grouped.values());
  }, [sortedObjects]);

  const sortOptions = [
    { value: 'title-asc' as SortOption, label: 'Title A-Z', icon: ArrowUp },
    { value: 'title-desc' as SortOption, label: 'Title Z-A', icon: ArrowDown },
    { value: 'date-desc' as SortOption, label: 'Newest First', icon: ArrowDown },
    { value: 'date-asc' as SortOption, label: 'Oldest First', icon: ArrowUp },
    { value: 'type-asc' as SortOption, label: 'Type A-Z', icon: ArrowUp },
    { value: 'type-desc' as SortOption, label: 'Type Z-A', icon: ArrowDown },
  ];

  const getCurrentSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || 'Newest First';
  };

  const addObjectOptions = useMemo(
    () => [
      {
        label: 'Photo',
        icon: Camera,
        color: getObjectTypePrimaryColor('image'),
        bgColor: getObjectTypeBackground('image'),
      },
    ],
    []
  );

  const renderObjectView = () => {
    if (!selectedObject && currentView !== 'attachment') {
      return null;
    }

    if (currentView === 'attachment') {
      if (!viewingAttachment) return null;

      const isImage =
        viewingAttachment.contentType.startsWith('image/') ||
        ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
          viewingAttachment.filename.split('.').pop()?.toLowerCase() || ''
        );
      const isPdf =
        viewingAttachment.contentType.includes('pdf') ||
        viewingAttachment.filename.toLowerCase().endsWith('.pdf');

      return (
        <div className="space-y-4 p-4 pb-6">
          <div className="flex items-center space-x-2 sticky top-0 bg-card pb-2 border-b flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToNote}
              className="h-8 w-8 p-0 rounded-full hover:bg-accent"
              title="Back to note"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
            </Button>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Viewing Attachment</h3>
              <p className="text-sm text-muted-foreground">From: {viewingAttachment.noteTitle}</p>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-card">
            <h4 className="font-medium mb-2">{viewingAttachment.filename}</h4>
            <p className="text-sm text-muted-foreground mb-4">Type: {viewingAttachment.contentType}</p>

            <div className="mb-6">
              {isImage ? (
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={viewingAttachment.url}
                    alt={viewingAttachment.filename}
                    className="w-full h-auto max-h-[70vh] object-contain bg-muted"
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const errorDiv = target.nextElementSibling as HTMLElement;
                      if (errorDiv) errorDiv.style.display = 'flex';
                    }}
                  />
                  <div className="bg-muted rounded-lg p-8 text-center hidden">
                    <FileImage className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Failed to load image</p>
                  </div>
                </div>
              ) : isPdf ? (
                <div
                  className="border rounded-lg overflow-hidden bg-muted relative"
                  style={{ height: '70vh' }}
                >
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(viewingAttachment.url)}&embedded=true`}
                    className="w-full h-full border-0"
                    title={viewingAttachment.filename}
                    onLoad={(e) => {
                      const iframe = e.target as HTMLIFrameElement;
                      const loadingDiv = iframe.nextElementSibling as HTMLElement;
                      if (loadingDiv) loadingDiv.style.display = 'none';
                    }}
                    onError={(e) => {
                      console.error(
                        'Error loading PDF with Google Docs viewer, trying direct embed:',
                        e
                      );
                      const iframe = e.target as HTMLIFrameElement;
                      const directUrl =
                        viewingAttachment.url +
                        (viewingAttachment.url.includes('?') ? '&' : '?') +
                        'view=FitH';
                      iframe.src = directUrl;

                      setTimeout(() => {
                        const loadingDiv = iframe.nextElementSibling as HTMLElement;
                        const errorDiv = loadingDiv?.nextElementSibling as HTMLElement;
                        if (loadingDiv) loadingDiv.style.display = 'none';
                        if (errorDiv) errorDiv.style.display = 'flex';
                      }, 3000);
                    }}
                  />
                  {/* Loading state */}
                  <div className="absolute inset-0 flex items-center justify-center bg-muted">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Loading PDF...</p>
                    </div>
                  </div>
                  {/* Error fallback */}
                  <div className="absolute inset-0 items-center justify-center bg-muted hidden">
                    <div className="text-center p-6">
                      <FileText className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-3">Unable to display PDF inline</p>
                      <a
                        href={viewingAttachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 underline"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download to view
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-muted rounded-lg p-8 text-center">
                  <File className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">File type not supported for viewing</p>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 pt-4 border-t sticky bottom-0 bg-card">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackToNote}
                className="touch-manipulation flex-1 sm:flex-none"
              >
                <ChevronRight className="h-3 w-3 mr-1 rotate-180" />
                Back to Note
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (!selectedObject) return null;

    switch (selectedObject.type) {
      case 'note': {
        const noteData = selectedObject.rawData as Note;

        return (
          <div className="space-y-4 p-4 pb-6">
            <div className="flex items-center space-x-2 sticky top-0 bg-card pb-2 border-b flex-shrink-0">
              <BackButton handleBackToList={handleBackToList} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Note Details</h3>
              </div>
            </div>
            <div className="border rounded-lg p-4 space-y-4 bg-card">
              {/* Note Title */}
              <div>
                <h4 className="font-semibold text-lg mb-1">{selectedObject.title}</h4>
                <p className="text-sm text-muted-foreground">
                  Created{' '}
                  {formatDistanceToNow(new Date(selectedObject.created_at), { addSuffix: true })}
                  {noteData?.created_by && ` by ${noteData.created_by}`}
                </p>
              </div>

              {/* Subject */}
              {noteData?.subject && (
                <div>
                  <h5 className="font-medium text-foreground mb-1">Subject</h5>
                  <p className="text-sm text-foreground">{noteData.subject}</p>
                </div>
              )}

              {/* Description */}
              {noteData?.description && (
                <div>
                  <h5 className="font-medium text-foreground mb-1">Description</h5>
                  <div className="text-sm text-foreground whitespace-pre-wrap">
                    {noteData.description}
                  </div>
                </div>
              )}

              {/* Reminder */}
              {(noteData?.reminder_date || noteData?.reminder_message) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <h5 className="font-medium text-yellow-800 mb-1 flex items-center">
                    <span className="mr-1">⏰</span>
                    Reminder
                  </h5>
                  {noteData.reminder_date && (
                    <p className="text-sm text-yellow-700">
                      <strong>Date:</strong> {new Date(noteData.reminder_date).toLocaleDateString()}
                      {noteData.reminder_time && ` at ${noteData.reminder_time}`}
                    </p>
                  )}
                  {noteData.reminder_message && (
                    <p className="text-sm text-yellow-700 mt-1">
                      <strong>Message:</strong> {noteData.reminder_message}
                    </p>
                  )}
                </div>
              )}

              {/* Visibility */}
              {noteData?.visible_to && noteData.visible_to.length > 0 && (
                <div>
                  <h5 className="font-medium text-foreground mb-1">Visible To</h5>
                  <div className="flex flex-wrap gap-1">
                    {noteData.visible_to.map((user, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {`${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim()}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <h5 className="font-medium text-foreground">
                      Attachments ({noteData?.attachments?.length || 0})
                    </h5>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsUploadingAttachment(true)}
                    className="h-7 w-7 p-0 rounded-full"
                    title="Add attachment"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>

                {noteData?.attachments && noteData.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {noteData.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-muted/50 border border-border rounded-lg hover:bg-accent transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {getFileIcon(attachment.original_filename, attachment.content_type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {attachment.original_filename}
                            </p>
                            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                              <span>{formatFileSize(attachment.file_size)}</span>
                              <span>•</span>
                              <span>{attachment.content_type}</span>
                              {attachment.uploaded_by && (
                                <>
                                  <span>•</span>
                                  <span>by {attachment.uploaded_by}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {/* View button for images and PDFs */}
                          {isViewableAttachment(
                            attachment.content_type,
                            attachment.original_filename
                          ) && (
                            <button
                              onClick={() => handleViewAttachment(attachment, selectedObject.title)}
                              className="p-2 text-muted-foreground hover:text-primary transition-colors"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          )}
                          {/* Download button */}
                          {/* TODO: Reinstate this in the future for paying customers */}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
                    <File className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No attachments yet</p>
                    <p className="text-xs text-muted-foreground">
                      Click the + button to add files or images
                    </p>
                  </div>
                )}

                {/* Attachment Upload Interface */}
                {isUploadingAttachment && (
                  <div
                    ref={attachmentUploadRef}
                    className="mt-4 p-4 border border-primary/20 rounded-lg bg-primary/5"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h6 className="font-medium text-foreground">Add New Attachment</h6>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIsUploadingAttachment(false);
                          setSelectedAttachmentFiles(null);
                        }}
                        className="h-6 w-6 p-0 rounded-full hover:bg-accent"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>

                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                        onChange={(e) => setSelectedAttachmentFiles(e.target.files)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 hover:bg-accent transition-colors">
                        <Upload className="h-6 w-6 mx-auto mb-2 text-primary" />
                        <p className="text-sm font-medium text-foreground">
                          Click to upload files or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Images, PDFs, Documents (DOC, DOCX, TXT, CSV, XLS, XLSX)
                        </p>
                      </div>
                    </div>

                    {selectedAttachmentFiles && selectedAttachmentFiles.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-foreground mb-2">
                          Selected Files ({selectedAttachmentFiles.length}):
                        </p>
                        <div className="space-y-1">
                          {Array.from(selectedAttachmentFiles).map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2 text-sm text-foreground bg-accent rounded px-2 py-1"
                            >
                              {file.type.startsWith('image/') ? (
                                <FileImage className="h-4 w-4 text-primary" />
                              ) : (
                                <FileText className="h-4 w-4 text-primary" />
                              )}
                              <span className="flex-1 truncate">{file.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={handleUploadAttachments}
                            disabled={createNoteAttachmentMutation.isPending}
                            className="flex-1"
                          >
                            {createNoteAttachmentMutation.isPending ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              'Upload Files'
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setIsUploadingAttachment(false);
                              setSelectedAttachmentFiles(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 pt-4 border-t sticky bottom-0 bg-card">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(selectedObject)}
                    className="touch-manipulation flex-1 sm:flex-none"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(selectedObject)}
                    className="text-destructive hover:text-destructive touch-manipulation flex-1 sm:flex-none"
                    disabled={deleteNoteMutation.isPending}
                  >
                    {deleteNoteMutation.isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
            </div>
          </div>
        );
      }

      case 'image': {
        const imageData = (selectedObject as HotspotObject & { rawData?: { url?: string } })
          ?.rawData;
        const imageUrl = imageData?.url;

        return (
          <div className="space-y-4 p-4 pb-6">
            <div className="flex items-center space-x-2 sticky top-0 bg-card pb-2 border-b flex-shrink-0">
              <BackButton handleBackToList={handleBackToList} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Image Details</h3>
              </div>
            </div>
            <div className="border rounded-lg p-4 bg-card">
              <h4 className="font-medium mb-2">{selectedObject.title}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Created{' '}
                {formatDistanceToNow(new Date(selectedObject.created_at), { addSuffix: true })}
              </p>
              <div className="mb-6">
                {imageUrl ? (
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={selectedObject.title}
                      className="w-full h-auto max-h-[60vh] sm:max-h-96 object-contain bg-muted touch-manipulation"
                      onError={(e) => {
                        console.error('Error loading image:', e);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.setAttribute('style', 'display: flex');
                      }}
                    />
                    <div className="bg-muted rounded-lg p-8 text-center hidden">
                      <Camera className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Failed to load image</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-muted rounded-lg p-8 text-center">
                    <Camera className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No image URL available</p>
                  </div>
                )}
              </div>
              {/* Edit Form or Action Buttons */}
              {isEditingImage && editingImageData ? (
                <div className="pt-4 border-t">
                  <ImageEditForm
                    hotspotId={editingImageData.hotspotId}
                    overlayId={editingImageData.overlayId}
                    currentTitle={editingImageData.title}
                    currentImageUrl={editingImageData.imageUrl}
                    onSuccess={async () => {
                      setIsEditingImage(false);
                      setEditingImageData(null);
                      if (onObjectUpdated) {
                        await onObjectUpdated();
                      }

                      handleBackToList();
                      toast({
                        title: 'Success',
                        description: 'Image updated successfully!',
                        variant: 'default',
                        className: 'bg-green-500 text-white',
                      });
                    }}
                    onCancel={() => {
                      setIsEditingImage(false);
                      setEditingImageData(null);
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center space-x-2 pt-4 border-t sticky bottom-0 bg-card">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handleShare(
                        selectedObject,
                        (selectedObject.rawData as { group_id?: number | null })?.group_id
                      )
                    }
                    className="touch-manipulation flex-1 sm:flex-none"
                  >
                    <Share2 className="h-3 w-3 mr-1" />
                    Share Hotspot
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(selectedObject)}
                    className="touch-manipulation flex-1 sm:flex-none"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(selectedObject)}
                    className="text-destructive hover:text-destructive touch-manipulation flex-1 sm:flex-none"
                    disabled={deleteOverlayMutation.isPending}
                  >
                    {deleteOverlayMutation.isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="space-y-4 p-4 pb-6">
            <div className="flex items-center space-x-2 sticky top-0 bg-card pb-2 border-b flex-shrink-0">
              <BackButton handleBackToList={handleBackToList} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold capitalize">
                  {selectedObject.type.replace('_', ' ')} Details
                </h3>
              </div>
            </div>
            <div className="border rounded-lg p-4 bg-card">
              <h4 className="font-medium mb-2">{selectedObject.title}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Created{' '}
                {formatDistanceToNow(new Date(selectedObject.created_at), { addSuffix: true })}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {selectedObject.type.charAt(0).toUpperCase() + selectedObject.type.slice(1)} details
                would be loaded here.
              </p>
              <div className="flex items-center space-x-2 pt-4 border-t sticky bottom-0 bg-card">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(selectedObject)}
                  className="touch-manipulation flex-1 sm:flex-none"
                  disabled={true}
                  title="Edit not available for this object type"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(selectedObject)}
                  className="text-destructive hover:text-destructive touch-manipulation flex-1 sm:flex-none"
                  disabled={true}
                  title="Delete not available for this object type"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderListView = () => (
    <div className="flex flex-col space-y-4 p-4 min-h-full">
      {/* Add New Object Section - Compact Icon Row */}
      <div className="border-b pb-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {addObjectOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.label}
                  onClick={handleAddObject}
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-white
                    transition-colors duration-200 hover:scale-105 active:scale-95
                    ${option.bgColor} shadow-sm touch-manipulation
                  `}
                  title={`Add ${option.label}`}
                >
                  <IconComponent className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Click an icon to add</p>
      </div>

      {/* Objects List */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">
            Attached Objects ({hotspotObjects?.length || 0})
          </h3>
          {hotspotObjects && hotspotObjects.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs h-7">
                  <ArrowUpDown className="h-3 w-3 mr-1" />
                  {getCurrentSortLabel()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {sortOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={`cursor-pointer ${sortBy === option.value ? 'bg-accent' : ''}`}
                    >
                      <IconComponent className="h-3 w-3 mr-2" />
                      {option.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Type Filter Pills */}
        {availableTypes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {availableTypes.map((type) => {
              const count = hotspotObjects?.filter((obj) => obj.type === type).length || 0;
              const isVisible = visibleTypes.has(type);
              const displayName = type.replace('_', ' ');

              return (
                <button
                  key={type}
                  onClick={() => toggleTypeVisibility(type)}
                  className={`
                    inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                    transition-colors duration-200 border
                    ${
                      isVisible
                        ? 'bg-accent text-accent-foreground border-border hover:bg-accent/80'
                        : 'bg-muted text-muted-foreground border-border hover:bg-accent'
                    }
                  `}
                >
                  <span className="capitalize">{displayName}</span>
                  <span className="ml-1 text-xs">({count})</span>
                </button>
              );
            })}
          </div>
        )}

        {objectsLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
            <p>Loading objects...</p>
          </div>
        ) : objectsError ? (
          <div className="text-center py-8 text-destructive">
            <p>Error loading objects</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchObjects()}
              className="mt-2 touch-manipulation"
            >
              Retry
            </Button>
          </div>
        ) : !hotspotObjects || hotspotObjects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Plus className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No objects attached to this hotspot yet.</p>
            <p className="text-sm">Use the colored icons above to add your first object.</p>

            {/* Delete Hotspot Button - Only show when no objects exist */}
            <div className="mt-6 pt-4 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteHotspot}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20 hover:border-destructive/30"
                disabled={deleteHotspotMutation.isPending}
              >
                {deleteHotspotMutation.isPending ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-3 w-3 mr-1" />
                    DELETE HOTSPOT
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : sortedObjects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Tag className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No objects match the current filters.</p>
            <p className="text-sm">Try enabling more object types in the filters above.</p>
          </div>
        ) : (
          <div className="space-y-3 pb-4">
            {(() => {
              const nonImageObjects = sortedObjects.filter((object) => object.type !== 'image');

              return (
                <>
                  {groupedImageObjects.map((group) => (
                    <div key={group.key} className="border rounded-lg p-3 bg-primary/5 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-2 min-w-0">
                          <Camera className="h-4 w-4 text-primary flex-shrink-0" />
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-foreground">
                              {group.groupName?.trim() ||
                                (group.groupId ? `Photo Group ${group.groupId}` : 'Ungrouped Photo')}
                            </h4>
                            <p className="text-xs text-muted-foreground">
                              {group.items.length} photo(s) attached to this group
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShareGroup(group.items, group.groupId)}
                          className="whitespace-nowrap"
                        >
                          <Share2 className="h-3 w-3 mr-1" />
                          Share Group
                        </Button>
                      </div>

                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {group.items.slice(0, 8).map((object) => {
                          const imageUrl = (object.rawData as { url?: string })?.url;
                          return (
                            <div key={`${object.type}-${object.id}`} className="relative">
                              <button
                                type="button"
                                onClick={() => handleObjectClick(object)}
                                className="relative w-full rounded-md overflow-hidden border bg-card hover:border-primary/50 transition-colors"
                                title={object.title}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={object.title || 'Hotspot photo'}
                                    className="w-full h-16 object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-16 flex items-center justify-center bg-muted text-muted-foreground">
                                    <Camera className="h-4 w-4" />
                                  </div>
                                )}
                              </button>
                              <div className="absolute top-1 right-1 flex gap-1">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(object);
                                  }}
                                  className="rounded bg-card/90 p-1 text-muted-foreground hover:text-destructive"
                                  title="Delete photo"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {group.items.length > 8 && (
                        <p className="text-xs text-muted-foreground">+{group.items.length - 8} more photo(s)</p>
                      )}

                      <p className="text-xs text-muted-foreground">
                        Sharing this group sends one link scoped to this selected photo group.
                      </p>
                    </div>
                  ))}

                  {nonImageObjects.map((object) => (
                    <div
                      key={`${object.type}-${object.id}`}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent active:bg-accent/80 cursor-pointer transition-colors touch-manipulation"
                      onClick={() => handleObjectClick(object)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleObjectClick(object);
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">{getObjectIcon(object.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium truncate">{object.title}</h4>
                            <Badge variant="outline" className={getObjectTypeColor(object.type)}>
                              {object.type.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Created{' '}
                            {formatDistanceToNow(new Date(object.created_at), { addSuffix: true })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(object.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );

  const handleDialogClose = useCallback(() => {
    setCurrentView('list');
    setSelectedObject(null);
    setIsEditing(false);
    setIsEditingImage(false);
    setEditingImageData(null);
    setIsUploadingAttachment(false);
    setSelectedAttachmentFiles(null);
    setShowAddObjectDialog(false);
    setShowShareDialog(false);
    setShareObject(null);
    setShareObjects([]);
    setShareGroupId(null);
    onClose();
  }, [onClose]);

  if (!hotspot) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent
          className="
          w-[95vw] max-w-2xl 
          h-[90vh] max-h-[90vh]
          sm:w-[90vw] sm:h-[85vh] sm:max-h-[85vh]
          md:w-[80vw] md:h-[80vh] md:max-h-[80vh]
          lg:max-w-2xl lg:h-[80vh]
          overflow-hidden bg-popover
          flex flex-col
        "
        >
          <DialogHeader className="flex-shrink-0 pb-4 border-b">
            <div className="flex items-center space-x-3">
              {currentView === 'list' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white bg-primary shadow-sm">
                  <Tag className="h-4 w-4" />
                </div>
              )}
              {currentView === 'list' ? (
                isEditingTitle ? (
                  // Editing mode
                  <div className="flex-1 flex items-center space-x-2">
                    <Input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      placeholder="Enter hotspot title..."
                      className="flex-1 text-lg font-semibold"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveTitle();
                        } else if (e.key === 'Escape') {
                          handleCancelTitleEdit();
                        }
                      }}
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSaveTitle}
                      disabled={updateHotspotMutation.isPending || !editingTitle.trim()}
                      className="h-8 w-8 p-0 rounded-full hover:bg-primary/10 text-primary hover:text-primary"
                      title="Save title"
                    >
                      {updateHotspotMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelTitleEdit}
                      className="h-8 w-8 p-0 rounded-full hover:bg-accent text-muted-foreground hover:text-foreground"
                      title="Cancel editing"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  // Display mode
                  <div className="flex items-center space-x-2">
                    <DialogTitle className="text-xl font-semibold capitalize">
                      {currentTitle || 'No Title'}
                    </DialogTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditTitle}
                      className="h-8 w-8 p-0 rounded-full hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                      title="Edit title"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  </div>
                )
              ) : (
                <DialogTitle className="text-xl font-semibold capitalize"></DialogTitle>
              )}
            </div>
            <DialogDescription className="text-sm text-muted-foreground hidden">
              {currentView === 'list'
                ? 'Manage notes, images, and labels attached to this hotspot'
                : 'View and manage this object'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y">
            {currentView === 'list' ? renderListView() : renderObjectView()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Photo Dialog */}
      <FABActionDialog
        open={showAddObjectDialog}
        setOpen={setShowAddObjectDialog}
        photoId={hotspot.photo_id}
        viewPosition={{
          pitch: hotspot.pitch,
          yaw: hotspot.yaw,
          hfov: 100,
        }}
        hotspotId={hotspot.id}
        onSuccess={async () => {
          setShowAddObjectDialog(false);
          await onObjectUpdated?.();
        }}
        onHotspotCreated={async () => {
          await onObjectUpdated?.();
        }}
      />

      {shareObject && hotspot && (
        <SharePhotoDialog
          open={showShareDialog}
          onClose={() => {
            setShowShareDialog(false);
            setShareObject(null);
            setShareObjects([]);
            setShareGroupId(null);
          }}
          object={shareObject}
          objects={shareObjects}
          overlayGroupId={shareGroupId}
          hotspot={hotspot}
          markerNumber={markerNumber}
          layoutImageUrl={layoutImageUrl}
          layoutImageDimensions={layoutImageDimensions}
          photoMarkers={photoMarkers}
          projectId={projectId}
        />
      )}
    </>
  );
};

export default HotspotObjectsDialog;
